import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ContentFrame, { PageHeading, SectionHeading } from '../components/assessment/ContentFrame'
import { ChoiceField, TextAnswer } from '../components/feedback/FeedbackInputs'
import {
  COMPLETION_TIME_BANDS,
  FORM_TEXT,
  SECTIONS,
  USABILITY_STATEMENTS,
} from '../components/feedback/feedbackFields'
import { errorMessage, getEnrolment, isOk, issueCertificate, submitFeedback } from '../helpers/trainingApi'

const ratingOptions = ({ high, low }) => [
  { value: '5', label: `5 (${high})` },
  { value: '4', label: '4' },
  { value: '3', label: '3' },
  { value: '2', label: '2' },
  { value: '1', label: `1 (${low})` },
]
const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

const CourseFeedback = () => {
  const { enrolmentId } = useParams()
  const navigate = useNavigate()
  const coursePath = `/training/course/${enrolmentId}`

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [data, setData] = useState(null)

  const [completionTime, setCompletionTime] = useState('')
  const [overallExperience, setOverallExperience] = useState('')
  const [multimediaQuality, setMultimediaQuality] = useState('')
  const [usability, setUsability] = useState({})
  const [additionalResources, setAdditionalResources] = useState('')
  const [topicsForMoreDepth, setTopicsForMoreDepth] = useState('')
  const [improvements, setImprovements] = useState(['', '', ''])
  const [postCompletionEngagement, setPostCompletionEngagement] = useState('')
  const [mostFrustrating, setMostFrustrating] = useState('')

  const [errors, setErrors] = useState([]) // [{ id, message }]
  const [submitError, setSubmitError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const headingRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getEnrolment(enrolmentId).then((res) => {
      if (cancelled) return
      if (isOk(res)) setData(res.data)
      else setLoadError(errorMessage(res, 'We could not load this course. Please try again.'))
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [enrolmentId])

  useEffect(() => {
    if (!loading) headingRef.current?.focus()
  }, [loading])

  const errorFor = (id) => errors.find((error) => error.id === id)?.message

  const validate = () => {
    const found = []
    if (!completionTime) found.push({ id: 'completionTime', message: 'Choose how long the course took you' })
    if (!overallExperience) {
      found.push({ id: 'overallExperience', message: 'Rate your overall online learning experience from 1 to 5' })
    }
    if (!multimediaQuality) {
      found.push({ id: 'multimediaQuality', message: 'Rate the quality of the multimedia content from 1 to 5' })
    }
    USABILITY_STATEMENTS.forEach(({ key, text }) => {
      if (!usability[key]) found.push({ id: `usability-${key}`, message: `Answer Yes or No: ${text}` })
    })
    return found
  }

  const focusField = (id) => {
    const field = document.getElementById(id)
    if (!field) return
    field.focus()
    field.scrollIntoView({ block: 'center' })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submitting) return
    setSubmitError('')
    const found = validate()
    setErrors(found)
    if (found.length) {
      // After the summary has rendered (it is announced as an alert).
      window.setTimeout(() => focusField(found[0].id), 0)
      return
    }

    setSubmitting(true)
    const res = await submitFeedback(enrolmentId, {
      completionTime,
      overallExperience: Number(overallExperience),
      multimediaQuality: Number(multimediaQuality),
      usability: Object.fromEntries(USABILITY_STATEMENTS.map(({ key }) => [key, usability[key] === 'yes'])),
      additionalResources: additionalResources.trim(),
      topicsForMoreDepth: topicsForMoreDepth.trim(),
      improvements: improvements.map((entry) => entry.trim()).filter(Boolean),
      postCompletionEngagement: postCompletionEngagement.trim(),
      mostFrustrating: mostFrustrating.trim(),
    })
    if (!isOk(res)) {
      setSubmitting(false)
      setSubmitError(errorMessage(res, 'We could not save your feedback. Please try again.'))
      return
    }
    // The certificate page issues the certificate itself if this call fails.
    await issueCertificate(enrolmentId)
    navigate(`${coursePath}/certificate`)
  }

  if (loading) {
    return (
      <ContentFrame>
        <Box role="status">
          <Typography sx={{ mb: 1 }}>Loading the feedback form…</Typography>
          <LinearProgress />
        </Box>
      </ContentFrame>
    )
  }

  if (loadError) {
    return (
      <ContentFrame>
        <PageHeading ref={headingRef}>{FORM_TEXT.title}</PageHeading>
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
        <RouterLink to="/mandatory-training">Back to my training</RouterLink>
      </ContentFrame>
    )
  }

  if (data.feedbackSubmitted) return <Navigate to={`${coursePath}/certificate`} replace />

  if (!data.enrolment?.passed) {
    return (
      <ContentFrame>
        <PageHeading ref={headingRef}>{FORM_TEXT.title}</PageHeading>
        <Typography sx={{ mb: 2 }}>
          The feedback form opens once you have passed the assessment. A certificate is only issued after passing.
        </Typography>
        <Button component={RouterLink} to={coursePath} variant="contained">
          Back to the course
        </Button>
      </ContentFrame>
    )
  }

  return (
    <ContentFrame>
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <PageHeading ref={headingRef}>{FORM_TEXT.title}</PageHeading>
        <Typography sx={{ mb: 1.5 }}>{FORM_TEXT.intro}</Typography>
        {data.accredited === true && <Typography sx={{ mb: 1.5 }}>{FORM_TEXT.accredited}</Typography>}
        <Typography sx={{ mb: 1.5 }}>{FORM_TEXT.thanks}</Typography>
        <Typography sx={{ mb: 1.5 }}>
          Questions 1 to 4 need an answer. Questions 5 to 9 are optional.
        </Typography>

        {errors.length > 0 && (
          <Alert severity="error" role="alert" sx={{ my: 2 }}>
            <Typography component="h2" sx={{ fontWeight: 700 }}>
              There is a problem
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {errors.map((error) => (
                <li key={error.id}>
                  <a
                    href={`#${error.id}`}
                    onClick={(event) => {
                      event.preventDefault()
                      focusField(error.id)
                    }}
                  >
                    {error.message}
                  </a>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        <SectionHeading>{SECTIONS.completionTime.heading}</SectionHeading>
        <ChoiceField
          id="completionTime"
          legend={SECTIONS.completionTime.question}
          options={COMPLETION_TIME_BANDS.map((band) => ({ value: band, label: band }))}
          value={completionTime}
          onChange={setCompletionTime}
          error={errorFor('completionTime')}
        />

        <SectionHeading>{SECTIONS.overallExperience.heading}</SectionHeading>
        <ChoiceField
          id="overallExperience"
          legend={SECTIONS.overallExperience.question}
          options={ratingOptions(SECTIONS.overallExperience)}
          value={overallExperience}
          onChange={setOverallExperience}
          error={errorFor('overallExperience')}
          row
        />

        <SectionHeading>{SECTIONS.multimediaQuality.heading}</SectionHeading>
        <ChoiceField
          id="multimediaQuality"
          legend={SECTIONS.multimediaQuality.question}
          options={ratingOptions(SECTIONS.multimediaQuality)}
          value={multimediaQuality}
          onChange={setMultimediaQuality}
          error={errorFor('multimediaQuality')}
          row
        />

        <SectionHeading>{SECTIONS.usability.heading}</SectionHeading>
        <Typography sx={{ mb: 1 }}>{SECTIONS.usability.question}</Typography>
        <Stack gap={1}>
          {USABILITY_STATEMENTS.map(({ key, text }) => (
            <Box key={key} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              <ChoiceField
                id={`usability-${key}`}
                legend={text}
                options={YES_NO}
                value={usability[key]}
                onChange={(answer) => setUsability((previous) => ({ ...previous, [key]: answer }))}
                error={errorFor(`usability-${key}`) ? 'Choose Yes or No' : undefined}
                row
              />
            </Box>
          ))}
        </Stack>

        <SectionHeading>{SECTIONS.additionalResources.heading}</SectionHeading>
        <TextAnswer
          id="additionalResources"
          question={SECTIONS.additionalResources.question}
          value={additionalResources}
          onChange={setAdditionalResources}
        />

        <SectionHeading>{SECTIONS.topicsForMoreDepth.heading}</SectionHeading>
        <TextAnswer
          id="topicsForMoreDepth"
          question={SECTIONS.topicsForMoreDepth.question}
          value={topicsForMoreDepth}
          onChange={setTopicsForMoreDepth}
        />

        <SectionHeading>{SECTIONS.improvements.heading}</SectionHeading>
        <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
          <Typography component="legend" sx={{ p: 0, mb: 1 }}>
            {SECTIONS.improvements.question}
          </Typography>
          <Stack gap={1.5}>
            {improvements.map((entry, entryIndex) => (
              <TextAnswer
                // eslint-disable-next-line react/no-array-index-key
                key={entryIndex}
                id={`improvement-${entryIndex + 1}`}
                label={`Improvement ${entryIndex + 1}`}
                value={entry}
                rows={1}
                onChange={(text) =>
                  setImprovements((previous) => previous.map((item, itemIndex) => (itemIndex === entryIndex ? text : item)))
                }
              />
            ))}
          </Stack>
        </Box>

        <SectionHeading>{SECTIONS.postCompletionEngagement.heading}</SectionHeading>
        <TextAnswer
          id="postCompletionEngagement"
          question={SECTIONS.postCompletionEngagement.question}
          value={postCompletionEngagement}
          onChange={setPostCompletionEngagement}
        />

        <SectionHeading>{SECTIONS.mostFrustrating.heading}</SectionHeading>
        <TextAnswer
          id="mostFrustrating"
          question={SECTIONS.mostFrustrating.question}
          value={mostFrustrating}
          onChange={setMostFrustrating}
        />

        <Typography sx={{ mt: 3, mb: 1.5 }}>{FORM_TEXT.closing}</Typography>
        <Typography sx={{ mb: 2 }}>
          Your answers are stored with your training record. A copy with your name removed is used to improve the
          course.
        </Typography>

        <Box aria-live="assertive">
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
        </Box>

        <Button type="submit" variant="contained" disabled={submitting}>
          {submitting ? 'Sending your feedback…' : 'Submit feedback and get my certificate'}
        </Button>
      </Box>
    </ContentFrame>
  )
}

export default CourseFeedback
